import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider, ClerkLoading, ClerkLoaded } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import "@/index.css";
import App from "@/App";
import ClerkBootFallback from "@/components/ClerkBootFallback";
import MarketingSite from "@/pages/marketing/MarketingSite";
import { getTenant } from "@/config/tenants";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const liveKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const devKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY_DEV;

// A Clerk PRODUCTION key is pinned to the production domain: on any other host every call to
// clerk.<domain> comes back 400, clerk-js never finishes loading, and <SignedIn>/<SignedOut>
// both render nothing — a blank white page. Preview and local hosts therefore run on the dev
// key instead. Real customer domains (including white-label portals, which are registered as
// Clerk satellite domains) keep the production key.
function isPreviewHost(host) {
  return host === "localhost" || host === "127.0.0.1" || host.endsWith(".emergentagent.com");
}

const clerkPubKey =
  liveKey?.startsWith("pk_live_") && devKey && isPreviewHost(window.location.hostname)
    ? devKey
    : liveKey;

if (!process.env.REACT_APP_BACKEND_URL) {
  throw new Error("REACT_APP_BACKEND_URL is not configured");
}
if (!clerkPubKey) {
  throw new Error("REACT_APP_CLERK_PUBLISHABLE_KEY is not configured");
}

// The app itself lives at /app (login + dashboard); everything else on the default
// (non-white-label) domain is the public marketing site, rendered WITHOUT ClerkProvider so it
// stays reachable even on domains Clerk production keys reject (e.g. OAuth app-review, or before
// DNS is fully cut over). A Clerk invitation/sign-in ticket in the query string always takes
// priority so accept-invite links keep working no matter which path they land on.
const pathname = window.location.pathname;
const hasClerkTicket = /__clerk_(ticket|status)/.test(window.location.search);
const isMarketingRoute = !getTenant().whiteLabel && !/^\/app(\/|$)/.test(pathname) && !hasClerkTicket;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {isMarketingRoute ? (
      <>
        <MarketingSite />
        <Toaster position="top-right" theme="light" />
      </>
    ) : (
      <ClerkProvider publishableKey={clerkPubKey} afterSignOutUrl="/app">
        <QueryClientProvider client={queryClient}>
          <ClerkLoading>
            <ClerkBootFallback />
          </ClerkLoading>
          <ClerkLoaded>
            <App />
          </ClerkLoaded>
          <Toaster position="top-right" theme="light" />
        </QueryClientProvider>
      </ClerkProvider>
    )}
  </React.StrictMode>,
);
